import React, { useState, useContext, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import DashboardHeader from '../components/Layout/DashboardHeader';
import { AuthContext } from '../contexts/AuthContext';
import supabase from '../config/supabaseClient';
import { useNavigate } from 'react-router-dom';

// Add this new component for Toast notifications
const Toast = ({ message, type, onClose }) => (
  <ToastContainer type={type}>
    <ToastMessage>{message}</ToastMessage>
    <ToastClose onClick={onClose}>×</ToastClose>
  </ToastContainer>
);

const SubscriptionAlerts = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [alertSettings, setAlertSettings] = useState({
    timePeriod: '1',
    timeUnit: 'Days Before',
    timeOfDay: '1:30am',
    contactName: '',
    monthlyReportTime: '8:00am',
    sendMonthlyReport: true
  });
  const [loading, setLoading] = useState(true);
  // Add state for toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Load existing settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('alert_settings')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGNF') throw error;
        
        if (data?.alert_settings) {
          setAlertSettings(data.alert_settings);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      loadSettings();
    }
  }, [user]);

  // Update the save function
  const saveAlertSettings = async () => {
    try {
      if (!alertSettings.contactName.trim()) {
        setToast({
          show: true,
          message: 'Please enter a contact name',
          type: 'error'
        });
        return;
      }

      // First, check if settings exist
      const { data: existingSettings } = await supabase
        .from('user_settings')
        .select('id')
        .eq('user_id', user.id)
        .single();

      let error;

      if (existingSettings) {
        // Update existing settings
        const { error: updateError } = await supabase
          .from('user_settings')
          .update({
            alert_settings: alertSettings,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', user.id);
        error = updateError;
      } else {
        // Insert new settings
        const { error: insertError } = await supabase
          .from('user_settings')
          .insert({
            user_id: user.id,
            alert_settings: alertSettings,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        error = insertError;
      }

      if (error) throw error;
      
      setToast({
        show: true,
        message: 'Settings saved successfully!',
        type: 'success'
      });
      
      // Navigate after a short delay to show the toast
      setTimeout(() => {
        navigate('/subscriptions');
      }, 1500);
    } catch (error) {
      console.error('Error saving settings:', error);
      setToast({
        show: true,
        message: `Failed to save settings: ${error.message}`,
        type: 'error'
      });
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <DashboardHeader />
        <ContentContainer>
          <LoadingSpinner>Loading settings...</LoadingSpinner>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <DashboardHeader />
      <ContentContainer>
        {toast.show && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        )}
        <AlertsForm>
          <Section>
            <SectionTitle>Default Alert</SectionTitle>
            
            <FormGroup>
              <Label>Time period</Label>
              <InputRow>
                <Select 
                  value={alertSettings.timePeriod}
                  onChange={(e) => setAlertSettings({...alertSettings, timePeriod: e.target.value})}
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                </Select>
                <Select
                  value={alertSettings.timeUnit}
                  onChange={(e) => setAlertSettings({...alertSettings, timeUnit: e.target.value})}
                >
                  <option value="Days Before">Days Before</option>
                  <option value="Weeks Before">Weeks Before</option>
                </Select>
              </InputRow>
            </FormGroup>

            <FormGroup>
              <Label>Time of day</Label>
              <Input 
                type="time"
                value={alertSettings.timeOfDay}
                onChange={(e) => setAlertSettings({...alertSettings, timeOfDay: e.target.value})}
              />
              <HelpText>This will be the default time for all alerts</HelpText>
            </FormGroup>

            <FormGroup>
              <Label>Contact name</Label>
              <Input
                type="text"
                placeholder="Enter contact name"
                value={alertSettings.contactName}
                onChange={(e) => setAlertSettings({...alertSettings, contactName: e.target.value})}
              />
            </FormGroup>
          </Section>

          <Section>
            <SectionTitle>Monthly Report</SectionTitle>
            
            <FormGroup>
              <Label>Time of day</Label>
              <Input 
                type="time"
                value={alertSettings.monthlyReportTime}
                onChange={(e) => setAlertSettings({...alertSettings, monthlyReportTime: e.target.value})}
              />
            </FormGroup>

            <FormGroup>
              
            </FormGroup>
          </Section>

          <SaveButton onClick={saveAlertSettings}>
            Save Changes
          </SaveButton>
        </AlertsForm>
      </ContentContainer>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background: var(--background);
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
`;

const AlertsForm = styled.div`
  background: white;
  padding: 32px;
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
`;

const Section = styled.div`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  color: var(--text-primary);
  margin: 0 0 24px 0;
  font-weight: 500;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.div`
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 8px;
  font-weight: 500;
`;

const InputRow = styled.div`
  display: flex;
  gap: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 16px;
  color: var(--text-primary);
  background: white;

  &::placeholder {
    color: #9CA3AF;
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 16px;
  color: var(--text-primary);
  background: white;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const HelpText = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  margin-top: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: var(--text-primary);
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
  }
`;

const SaveButton = styled.button`
  background: #5CCBCB;
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  float: right;

  &:hover {
    opacity: 0.9;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

// Add these styled components
const slideIn = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 24px;
  right: 24px;
  padding: 16px 24px;
  border-radius: 8px;
  background: ${props => props.type === 'success' ? '#10B981' : '#EF4444'};
  color: white;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: ${slideIn} 0.3s ease-out;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ToastMessage = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const ToastClose = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

export default SubscriptionAlerts; 